import { useMemo, useState } from 'react';
import { Divider, Radio, RadioGroup, Slider } from '@nextui-org/react';
import { Line } from 'react-chartjs-2';
import { ChartDataset } from 'chart.js';
import { useAppSelector } from '../../store/hooks';
import { selectMessagesWithClient } from '../../store/chat/chatSlice';

const MAX_LABEL_LEN = 20;
const AXIS_TIMESTAMP = 'timestamp';
const AXIS_MESSAGE = 'message';

export default function ClientProfileSentimentChart() {
  const messagesWithClient = useAppSelector(selectMessagesWithClient);
  const [sentimentAxisX, setSentimentAxisX] = useState(AXIS_MESSAGE);
  const [smoothFactor, setSmoothFactor] = useState(0.1);

  const messagesFromClient = useMemo(
    () => messagesWithClient.filter((m) => m.role === 'assistant'),
    [messagesWithClient]
  );

  const sentimentMessageLabels = useMemo(
    () =>
      messagesFromClient
        .map((m) => m.content.trim())
        .map((s) =>
          s.length > MAX_LABEL_LEN ? s.slice(0, MAX_LABEL_LEN - 3) + '...' : s
        ),
    [messagesFromClient]
  );

  const sentimentTimestampLabels = useMemo(
    () => messagesFromClient.map((m) => Date.parse(m.createdAt)),
    [messagesFromClient]
  );

  const sentimentChartDatasets: ChartDataset<'line', (number | undefined)[]>[] =
    useMemo(() => {
      const sentiments = messagesFromClient.map((m) => m.contentSentiment);

      // Brown's linear exponential smoothing adapted from:
      // https://en.wikipedia.org/wiki/Exponential_smoothing
      const sentimentsSmoothed = sentiments.slice(0, 1);
      if (sentiments.length > 1) {
        let x1 = sentiments[0] ?? 0;
        let x2 = x1;
        for (const s of sentiments.slice(1)) {
          x1 = smoothFactor * (s ?? 0) + (1 - smoothFactor) * x1;
          x2 = smoothFactor * x1 + (1 - smoothFactor) * x2;
          sentimentsSmoothed.push(2 * x1 - x2);
        }
      }

      return [
        {
          label: 'Message sentiment (mood)',
          data: sentiments,
          cubicInterpolationMode: 'monotone',
        },
        {
          label: 'Trendline (exponential smoothing)',
          data: sentimentsSmoothed,
          cubicInterpolationMode: 'monotone',
          borderDash: [5, 5],
        },
      ];
    }, [messagesFromClient, smoothFactor]);

  return (
    <div className="flex flex-col gap-2">
      <Slider
        size="sm"
        label="Exponential smoothing factor"
        minValue={0}
        maxValue={1}
        step={0.01}
        value={smoothFactor}
        onChange={(value) =>
          setSmoothFactor((prev) =>
            typeof value === 'number' ? value : value.pop() || prev
          )
        }
      />
      <Divider />
      {sentimentAxisX === AXIS_MESSAGE && (
        <Line
          data={{
            labels: sentimentMessageLabels,
            datasets: sentimentChartDatasets,
          }}
          options={{
            scales: {
              y: { min: -1, max: 1, ticks: { maxRotation: 90 } },
            },
          }}
        />
      )}
      {sentimentAxisX === AXIS_TIMESTAMP && (
        <Line
          data={{
            labels: sentimentTimestampLabels,
            datasets: sentimentChartDatasets,
          }}
          options={{
            scales: {
              x: { type: 'time', time: { tooltipFormat: 'DATE_MED' } },
              y: { min: -1, max: 1 },
            },
          }}
        />
      )}
      <Divider />
      <RadioGroup
        size="sm"
        orientation="horizontal"
        label="x-Axis"
        value={sentimentAxisX}
        onValueChange={setSentimentAxisX}
      >
        <Radio value={AXIS_MESSAGE}>Message</Radio>
        <Radio value={AXIS_TIMESTAMP}>Timestamp</Radio>
      </RadioGroup>
    </div>
  );
}
